import type { IconName } from '@webapp2/shared/components/icons/material-icon'
import { userCoursePerformanceId, userSlimId } from '@webapp2/shared/model'
import { ref } from 'vue'
import {
  createRouter,
  createWebHistory,
  type RouteLocation,
  type RouteLocationNormalizedGeneric,
  type RouteLocationRaw,
  type RouteRecordRaw
} from 'vue-router'
import {
  ensureModuleBuildSettings,
  ensurePlayerBuildSettings,
  ensurePlayerBuilds
} from './admin/services/admin-builds-service'
import { initialAdminUpdate, setAdminStateFromCache } from './admin/services/admin-entity-service'
import { handleAdminNavigation } from './admin/services/admin-service'
import { ensureUser, ensureUserSlims, loadHealthStreamStudents } from './admin/services/admin-user-service'
import { adminState, type AdminPlayerRun } from './admin/state'
import { config, updateGlobalServiceWorker } from './main'
import { postGetBody } from './services/api'
import { handleNavigation as handleAppNavigation } from './services/app-service'
import { authenticate } from './services/authentication-service'
import { ensureCurrentPlayerBuild, ensureModuleBuilds, ensurePlayerBuild } from './services/build-service'
import { registerModuleBuildForCourse } from './services/course-service'
import { initialUpdate, setStateFromCache, updateEntities } from './services/entity-service'
import {
  checkForValidAuthCode,
  handleHealthStreamAuthCode,
  loginWithHealthstream
} from './services/healthstream-service'
import { hasAdminPermission, isAdmin } from './services/permissions-service'
import { initQuestBridge } from './services/quest-bridge'
import { prebriefId, state, type PlayerTechnique, type Prebrief } from './state'

export type Layout = 'app' | 'admin' | 'auth'
export type BriefSection = 'prebrief' | 'debrief'

export type RouteMeta = { layout: Layout }
export type OssoMeta = {
  hideNav?: boolean
  hideOnWeb?: boolean
  loader?: (to: RouteLocationNormalizedGeneric) => void | RouteLocationRaw | Promise<void | RouteLocationRaw>
}
export type BriefMeta = OssoMeta & {
  icon: IconName
  title: string
  nextText?: string
  hidden?: boolean
  showSkip?: boolean
  hideRetry?: boolean
  section: BriefSection
}
export type AdminMeta = OssoMeta & {
  icon: IconName
  title: string
  hide?: () => boolean
}

export type OssoRoute = Omit<RouteRecordRaw, 'meta' | 'children'> & {
  meta?: OssoMeta | BriefMeta | AdminMeta
  children?: OssoRoute[]
}

export type OssoCourseRoute = Omit<OssoRoute, 'meta'> & {
  meta: BriefMeta
}
export type OssoAdminRoute = Omit<OssoRoute, 'meta'> & {
  meta: AdminMeta
  name: string
}

export type OssoRouteLocation = Omit<RouteLocation, 'meta' | 'name'> & {
  meta: RouteMeta | (RouteMeta & BriefMeta) | (RouteMeta & AdminMeta)
  name: string
}

export type AdminRouteLocation = Omit<OssoRouteLocation, 'meta'> & {
  meta: RouteMeta & AdminMeta
}

export type OrganizationRouteParams = { organizationId: string }
export type IdRouteParams = OrganizationRouteParams & { id: string }
export type CourseRouteParams = OrganizationRouteParams & { courseId: string }

export type CourseRouteLocation = OssoRouteLocation & { params: CourseRouteParams }
export type DebriefRouteLocation = OssoRouteLocation & { params: CourseRouteParams & { runId: string } }
export type IdRouteLocation = OssoRouteLocation & { params: IdRouteParams }

const orgPrefix = '/org/:organizationId'

export const appRoutes = createRoutes('app', {
  root: {
    path: '/',
    name: 'root',
    component: () => import('./views/CourseListView.vue'),
    beforeEnter: () => {
      if (state.organizationId) {
        return { name: 'courseList', params: { organizationId: state.organizationId } }
      } else {
        return { name: 'login' }
      }
    }
  },
  courseList: {
    path: orgPrefix,
    component: () => import('./views/CourseListView.vue'),
    meta: {
      loader: () => updateEntities(true)
    }
  },
  performance: {
    path: `${orgPrefix}/performance`,
    component: () => import('./views/MyPerformance.vue')
  },
  manageDownloads: {
    path: `${orgPrefix}/manage-downloads`,
    component: () => import('./views/ManageDownloadsView.vue'),
    beforeEnter: async () => {
      await initialUpdate
      if (!state.user?.isOsso || !state.vuplex) {
        return { name: 'root' }
      }
    }
  },
  collab: {
    path: `${orgPrefix}/collaborative`,
    component: () => import('./views/CollaborativeView.vue'),
    meta: { hideOnWeb: true }
  },
  host: {
    path: `${orgPrefix}/host`,
    component: () => import('./views/HostView.vue'),
    meta: { hideOnWeb: true }
  },
  join: {
    name: 'join',
    path: `${orgPrefix}/join/:id`,
    component: () => import('./views/JoinView.vue'),
    meta: {
      hideOnWeb: true,
      loader: async to => {
        await initialUpdate
        const session = state.multiplayerSessions[to.params.id as string]
        registerModuleBuildForCourse(session!.courseId)
      }
    }
  },
  prebrief: {
    path: `${orgPrefix}/courses/:courseId`,
    component: () => import('./layout/CourseLayout.vue'),
    name: 'prebrief',
    meta: {
      hideNav: true,
      loader: async to => {
        await initialUpdate
        const courseParam = to.params.courseId as string
        const isHealthStream = to.query['healthstream']
        const courseId = isHealthStream
          ? Object.values(state.courses).find(x => x.healthStreamCourseId == courseParam)?.id
          : courseParam
        const organizationId = isHealthStream ? state.organizationId : to.params.organizationId
        const course = state.courses[courseId!]
        if (!course) {
          return { name: 'courseList', params: { organizationId } }
        }
        registerModuleBuildForCourse(course.id)

        if (isHealthStream) {
          return { name: to.name, params: { organizationId, courseId }, query: {} }
        }
      }
    },
    redirect: () => {
      return { name: 'prebrief-welcome' }
    },
    children: createCourseChildren('prebrief', [
      {
        path: 'welcome',
        name: 'prebrief-welcome',
        component: () => import('./views/prebrief-section/WelcomeView.vue'),
        meta: { icon: 'home', title: 'Welcome', nextText: "Let's start this journey!" }
      },
      {
        path: 'learning',
        name: 'prebrief-learning-objectives',
        component: () => import('./views/prebrief-section/LearningObjectivesView.vue'),
        meta: { icon: 'school-cap', title: 'Learning Objectives', nextText: "Great! What's next?" }
      },
      {
        path: 'briefing',
        name: 'prebrief-scenario-briefing',
        component: () => import('./views/prebrief-section/ScenarioBriefingView.vue'),
        meta: { icon: 'clip-board', title: 'Scenario Briefing', nextText: "I've got it!" }
      },
      {
        path: 'scenario',
        name: 'prebrief-scenario-details',
        component: () => import('./views/prebrief-section/ScenarioDetailsView.vue'),
        meta: { icon: 'bulb', title: 'Scenario Details', nextText: "Let's go!" }
      },
      {
        path: 'simulation',
        name: 'prebrief-simulation',
        component: () => import('./views/prebrief-section/StartSimulationView.vue'),
        meta: { icon: 'qr-scan', title: 'Start Simulation', nextText: 'Ready to learn!' }
      }
    ])
  },
  debrief: {
    component: () => import('./layout/CourseLayout.vue'),
    path: `${orgPrefix}/debrief/:runId`,
    name: 'debrief',
    redirect: () => {
      return { name: 'debrief-survey' }
    },
    beforeEnter: to => {
      const debriefRoutes = appRoutes.debrief.children
      const run = state.runResults[to.params.runId as string]
      debriefRoutes
        .filter(x => x.name !== 'debrief-overview')
        .forEach(x => {
          if (!run || !state.user) {
            return
          }
          const userCoursePerformance = run.courseId
            ? state.userCoursePerformances[userCoursePerformanceId(state.organizationId!, run.courseId, state.user.id)]
            : undefined
          const isCollab = run.multiplayerSessionId !== undefined
          x.meta = {
            ...(x.meta as BriefMeta),
            hidden:
              x.name === 'debrief-lead-placement-analytics' || //Always hide analytics until we implement
              (x.name === 'debrief-performance-history' && (!userCoursePerformance || isCollab))
          }
        })
    },
    meta: { hideNav: true },
    children: createCourseChildren('debrief', [
      {
        path: 'survey',
        name: 'debrief-survey',
        component: () => import('./views/debrief-section/SurveyView.vue'),
        meta: { icon: 'note', title: 'Survey', nextText: 'Submit', hideRetry: true, showSkip: true }
      },
      {
        path: 'overview',
        name: 'debrief-overview',
        component: () => import('./views/debrief-section/Overview.vue'),
        meta: { icon: 'trophy', title: 'Overview' }
      },
      {
        path: 'analytics',
        name: 'debrief-lead-placement-analytics',
        component: () => import('./views/debrief-section/LeadPlacementView.vue'),
        beforeEnter: to => {
          return {
            name: 'debrief-breakdown-by-state',
            params: to.params
          }
        },
        meta: { icon: 'bar-chart', title: 'Lead Placement Analytics' }
      },
      {
        path: 'breakdown',
        name: 'debrief-breakdown-by-state',
        component: () => import('./views/debrief-section/BreakdownByStageView.vue'),
        meta: { icon: 'clipboard', title: 'Breakdown by Stage' }
      },
      {
        path: 'reflection',
        name: 'debrief-reflection-questions',
        component: () => import('./views/debrief-section/ReflectionQuestionsView.vue'),
        meta: { icon: 'bulb-question', title: 'Reflection Questions' }
      },
      {
        path: 'domain',
        name: 'debrief-domain-scores',
        component: () => import('./views/debrief-section/DomainScoresView.vue'),
        meta: { icon: 'star', title: 'Domain Scores' }
      },
      {
        path: 'history',
        name: 'debrief-performance-history',
        component: () => import('./views/debrief-section/PerformanceHistoryView.vue'),
        meta: { icon: 'history', title: 'Performance History' }
      }
    ])
  },
  qr: {
    name: 'qr',
    path: '/qr',
    component: () => import('./views/QrView.vue')
  }
})

export const adminTechniqueRoutes = createRoutes('admin', {
  'admin-technique-details-info': {
    path: 'details',
    name: 'admin-technique-details-info',
    component: () => import('./admin/views/Technique/DetailsView.vue'),
    meta: { icon: 'edit', title: 'Details' }
  },
  'admin-technique-prebrief': {
    path: 'prebrief',
    name: 'admin-technique-prebrief',
    component: () => import('./admin/views/Technique/PrebriefView.vue'),
    meta: { icon: 'editClipboard', title: 'Prebrief' },
    beforeEnter: async to => {
      const techniqueId = to.params.id as string
      const id = prebriefId(techniqueId)
      const request = { type: 'Prebrief', ids: [id] }
      const prebriefTask = postGetBody<Prebrief[]>('admin/documents-by-id', request).then(prebriefs => {
        if (prebriefs?.[0]) {
          state.prebriefs[id] = prebriefs?.[0]
        }
      })
      if (!state.prebriefs[id]) {
        await prebriefTask
      }
    }
  }
})

export const adminRoutes = createRoutes('admin', {
  // Prevents error in tests and running locally when navigating to portal
  portal: {
    path: '/portal/:orgId?',
    component: { render: () => null }
  },
  admin: {
    beforeEnter: async () => {
      await initialUpdate
      if (!isAdmin.value) {
        return { name: 'root' }
      }
    },
    redirect: () => {
      return { name: 'admin-installed' }
    },
    path: '/admin',
    component: () => import('./admin/Provider.vue'),
    children: [
      {
        path: 'installed',
        name: 'admin-installed',
        component: () => import('./admin/views/InstalledModulesView.vue'),
        meta: { icon: 'packageDown', title: 'Installed Modules' }
      },
      {
        path: 'module-builds',
        name: 'admin-module-builds',
        redirect: to => ({ name: 'admin-module-builds-list', query: to.query }),
        meta: { icon: 'webBox', title: 'Module Builds' },
        children: [
          {
            path: '',
            name: 'admin-module-builds-list',
            component: () => import('./admin/views/ModuleBuildsListView.vue')
          },
          {
            path: ':id/details',
            name: 'admin-module-build-details',
            component: () => import('./admin/views/ModuleBuild/DetailsView.vue'),
            meta: {
              loader: async to => {
                const moduleBuildId = to.params.id as string
                await Promise.all([ensureModuleBuilds([moduleBuildId]), ensureModuleBuildSettings([moduleBuildId])])
                const moduleBuild = state.moduleBuilds[moduleBuildId]
                if (!moduleBuild) {
                  return { name: 'admin-module-builds-list' }
                } else if (moduleBuild.serverBuildId) {
                  await ensureModuleBuilds([moduleBuild.serverBuildId])
                }
              }
            }
          }
        ]
      },
      {
        path: 'multiplayer',
        name: 'admin-multiplayer',
        component: () => import('./admin/views/MultiplayerSessionsListView.vue'),
        meta: { icon: 'multiplayer', title: 'Multiplayer' }
      },
      {
        path: 'runs',
        name: 'admin-runs',
        redirect: to => ({ name: 'admin-runs-list', query: to.query }),
        meta: { icon: 'runs', title: 'Runs' },
        children: [
          {
            path: '',
            name: 'admin-runs-list',
            component: () => import('./admin/views/RunsListView.vue')
          },
          {
            path: ':id/details',
            name: 'admin-run-details',
            component: () => import('./admin/views/RunDetailsView.vue'),
            beforeEnter: async to => {
              const runId = to.params.id as string
              if (!adminState.playerRuns[runId]) {
                const request = { type: 'PlayerRun', ids: [runId] }
                const runResponse = await postGetBody<AdminPlayerRun[]>('admin/documents-by-id', request)
                const adminPlayerRun = runResponse?.[0]
                if (adminPlayerRun) {
                  adminState.playerRuns[runId] = adminPlayerRun
                } else {
                  return { name: 'admin-runs-list' }
                }
              }
              const run = adminState.playerRuns[runId]
              await Promise.all([
                ensurePlayerBuild(run.playerBuildId),
                run.moduleBuildId ? ensureModuleBuilds([run.moduleBuildId]) : Promise.resolve(),
                ensureUserSlims([userSlimId(run.userId)])
              ])
            }
          }
        ]
      },
      {
        path: 'techniques',
        name: 'admin-techniques',
        redirect: () => ({ name: 'admin-techniques-list' }),
        meta: { icon: 'clipboard', title: 'Techniques' },
        children: [
          {
            path: '',
            name: 'admin-techniques-list',
            component: () => import('./admin/views/TechniquesListView.vue'),
            meta: { icon: 'clipboard', title: 'Techniques' }
          },
          {
            path: ':id',
            name: 'admin-technique-details',
            component: () => import('./admin/views/TechniqueView.vue'),
            meta: { icon: 'clipboard', title: 'Technique Details' },
            redirect: () => ({ name: 'admin-technique-details-info' }),
            beforeEnter: async to => {
              const techniqueId = to.params.id as string
              const request = { type: 'PlayerTechnique', ids: [techniqueId] }
              const playerTechniqueTask = postGetBody<PlayerTechnique[]>('admin/documents-by-id', request).then(
                playerTechniques => {
                  if (playerTechniques?.[0]) {
                    state.playerTechniques[techniqueId] = playerTechniques?.[0]
                  }
                }
              )
              if (!state.playerTechniques[techniqueId]) {
                await playerTechniqueTask
              }
            },
            children: Object.values(adminTechniqueRoutes)
          }
        ]
      },
      {
        path: 'player-builds',
        name: 'admin-player-builds',
        redirect: to => ({ name: 'admin-player-builds-list', query: to.query }),
        meta: { icon: 'player-builds', title: 'Player Builds' },
        children: [
          {
            path: '',
            name: 'admin-player-builds-list',
            component: () => import('./admin/views/PlayerBuildsListView.vue'),
            meta: { icon: 'player-builds', title: 'Player Builds' }
          },
          {
            path: ':id/details',
            name: 'admin-player-build-details',
            component: () => import('./admin/views/PlayerBuildDetailsView.vue'),
            meta: {
              loader: async to => {
                const id = to.params.id as string
                await Promise.all([ensurePlayerBuilds([id]), ensurePlayerBuildSettings([id])])
                const playerBuild = adminState.playerBuilds[id]
                if (!playerBuild) {
                  return { name: 'admin-module-builds-list' }
                } else if (playerBuild.serverBuildId) {
                  await ensurePlayerBuilds([playerBuild.serverBuildId])
                }
              }
            }
          }
        ]
      },
      {
        path: 'users',
        name: 'admin-users',
        redirect: to => ({ name: 'admin-users-list', query: to.query }),
        meta: {
          icon: 'people',
          title: 'Users',
          hide: () => !hasAdminPermission('Users')
        },
        children: [
          {
            path: '',
            name: 'admin-users-list',
            component: () => import('./admin/views/UsersListView.vue'),
            beforeEnter: async () => {
              if (!hasAdminPermission('Users')) {
                return { name: 'admin-module-builds' }
              }
              await initialAdminUpdate
            }
          },
          {
            path: 'add',
            name: 'admin-user-add',
            component: () => import('./admin/views/UserDetailView.vue'),
            beforeEnter: () => {
              if (!hasAdminPermission('Users') || !hasAdminPermission('Organizations.AllPermissions')) {
                return { name: 'admin-users-list' }
              }
            }
          },
          {
            path: ':id/details',
            name: 'admin-user-details',
            component: () => import('./admin/views/UserDetailView.vue'),
            beforeEnter: () => {
              if (!hasAdminPermission('Users')) {
                return { name: 'admin-module-builds' }
              }
            },
            meta: {
              loader: async to => {
                const id = to.params.id as string
                await Promise.all([ensureUser(id), loadHealthStreamStudents(id)])
                if (!adminState.users[id]) {
                  return { name: 'admin-users-list' }
                }
              }
            }
          }
        ]
      }
    ]
  }
})

export const authRoutes = createRoutes('auth', {
  login: {
    path: '/login',
    component: () => import('./auth/views/LoginOptionsView.vue'),
    beforeEnter: to => {
      if (!state.vuplex?.qrReaderAvailable) {
        return { name: 'loginIdp', query: to.query }
      }
    }
  },
  loginIdp: {
    path: '/login-idp',
    beforeEnter: to => {
      if (!config.enableHealthStreamLogin) {
        return { name: 'loginOsso' }
      }
      if (state.vuplex) {
        to.meta.backRoute = authRoutes.login
      }
    },
    component: () => import('./auth/views/LoginIdpView.vue')
  },
  loginOsso: {
    path: '/login-osso',
    component: () => import('./auth/views/OssoLoginView.vue'),
    beforeEnter: to => {
      to.meta.backRoute = config.enableHealthStreamLogin
        ? authRoutes.loginIdp
        : state.vuplex?.qrReaderAvailable
          ? authRoutes.login
          : undefined
    }
  },
  resetPassword: {
    path: '/reset-password',
    component: () => import('./auth/views/ResetPassword.vue'),
    beforeEnter: to => {
      to.meta.backRoute = authRoutes.loginOsso
    }
  },
  loginQrHelp: {
    path: '/login-qr-help',
    component: () => import('./auth/views/LoginQrHelpView.vue'),
    beforeEnter: to => {
      to.meta.backRoute = authRoutes.login
    }
  },
  verifyQrCode: {
    path: '/verify-qr-code',
    component: () => import('./auth/views/VerifyQrCode.vue'),
    beforeEnter: to => {
      to.meta.backRoute = authRoutes.login
    }
  },
  permissionsDenied: {
    path: '/permissions-denied',
    component: () => import('./auth/views/PermissionsDenied.vue')
  },
  healthStreamMissingLogininfo: {
    path: '/healthstream-first-login-failure',
    component: () => import('./auth/views/HealthStreamMissingLoginInfoView.vue'),
    beforeEnter: to => {
      to.meta.backRoute = authRoutes.login
    }
  }
})

const redirects: RouteRecordRaw[] = [
  {
    path: '/healthstream-course',
    redirect: to => {
      const healthStreamOrganizationId = to.query['org-id'] as string
      const healthStreamCourseId = to.query['course-id'] as string

      const redirect = router.resolve({
        name: 'prebrief',
        params: { organizationId: healthStreamOrganizationId, courseId: healthStreamCourseId },
        query: { healthstream: 'true' }
      })

      localStorage.setItem('redirect', redirect.href)
      localStorage.setItem('healthStreamOrganizationId', healthStreamOrganizationId)
      localStorage.setItem('healthStreamCourseId', healthStreamCourseId)
      localStorage.setItem('healthStreamOssoCourseId', to.query['osso-course-id'] as string)
      localStorage.setItem('healthStreamUserId', to.query['user-id'] as string)

      loginWithHealthstream()
      // Early return to prevent redirect from navigating before health stream redirect
      return undefined as unknown as RouteLocationRaw
    },
    meta: { externalAuth: true }
  },
  {
    path: '/healthstream',
    component: { render: () => null },
    beforeEnter: async to => {
      const authCode = checkForValidAuthCode(to.query)
      if (authCode) {
        const path = await handleHealthStreamAuthCode(authCode)
        firstNavigation = false
        authenticateFinished()
        if (path.startsWith('/portal')) {
          location.href = path
          return undefined as unknown as RouteLocationRaw
        }
        return router.resolve(path)
      }
      return { name: '' }
    },
    meta: { externalAuth: true }
  }
]

function createRoutes<T extends { [K in keyof T]: OssoRoute | OssoCourseRoute }>(
  layout: Layout,
  routes: { [K in keyof T]: T[K] }
) {
  return Object.keys(routes).reduce(
    (acc, key) => {
      const route = routes[key as keyof T]
      const meta = { ...route.meta, layout }
      acc[key as keyof T] = { ...route, name: key, meta }
      return acc
    },
    <{ [K in keyof T]: T[K] & { name: string; meta: RouteMeta } }>{}
  )
}

type BriefChildRoute = Omit<OssoRoute, 'meta'> & {
  meta: Omit<BriefMeta, 'section'>
}
function createCourseChildren(section: BriefSection, children: BriefChildRoute[]): OssoRoute[] {
  return children.map(x => ({
    ...x,
    meta: {
      ...x.meta,
      section
    }
  }))
}

export const router = createRouter({
  history: createWebHistory('/'),
  routes: [...redirects, ...Object.values(appRoutes), ...Object.values(adminRoutes), ...Object.values(authRoutes)]
})

export const isNavigating = ref(false)

let firstNavigation = true
router.beforeEach(async (to, from) => {
  isNavigating.value = true
  await checkForUpdate(to, from)

  if (to.meta.externalAuth) {
    return
  }

  const authRedirect = await handleFirstNavigation(to)
  if (shouldUseHealthStreamLogin(to, authRedirect)) {
    localStorage.setItem('redirect', stripHsLoginQuery(to.fullPath))
    loginWithHealthstream()
    return undefined as unknown as RouteLocationRaw
  }

  if (shouldHandoffToPortal(to, authRedirect)) {
    location.href = stripHsLoginQuery(to.fullPath)
    return undefined as unknown as RouteLocationRaw
  }

  handleAdminNavigation()
  const appRedirect = await handleAppNavigation(to.path)
  if (appRedirect) {
    return appRedirect
  }

  if (to.meta.hideOnWeb && !state.vuplex) {
    return '/'
  }

  if (!state.permissionsAccepted && to.name !== authRoutes.permissionsDenied.name) {
    return authRoutes.permissionsDenied
  }

  if (authRedirect) {
    return authRedirect
  }

  const loader = to.meta.loader as OssoMeta['loader']
  const redirect = loader ? await loader(to) : undefined
  if (redirect) return redirect
})

function shouldUseHealthStreamLogin(to: RouteLocationNormalizedGeneric, authRedirect?: RouteLocationRaw) {
  return hasHsLoginQuery(to) && authRedirect === authRoutes.login.path
}

function shouldHandoffToPortal(to: RouteLocationNormalizedGeneric, authRedirect?: RouteLocationRaw) {
  return hasHsLoginQuery(to) && !authRedirect && to.path.startsWith('/portal')
}

function hasHsLoginQuery(to: RouteLocationNormalizedGeneric) {
  return 'hslogin' in to.query
}

function stripHsLoginQuery(fullPath: string) {
  const url = new URL(fullPath, window.location.origin)
  url.searchParams.delete('hslogin')
  const query = url.searchParams.toString()
  // /portal/:orgId? matches /portal/ as orgId="" — collapse to /portal so the optional segment is treated as absent
  const path = url.pathname === '/portal/' ? '/portal' : url.pathname
  return path + (query ? `?${query}` : '')
}

async function handleFirstNavigation(to: RouteLocationNormalizedGeneric) {
  if (!firstNavigation) {
    return undefined
  }
  firstNavigation = false
  await initQuestBridge()
  await setStateFromCache()
  await setAdminStateFromCache()
  if (!state.permissionsAccepted) {
    authenticateFinished()
    return authRoutes.permissionsDenied
  }
  // this needs to go here rather than app onMounted because beforeEnter guards run before that
  // and we want to ensure required data is loaded to do routing checks, eg entity exists
  const authPath = await authenticate(to as OssoRouteLocation)
  authenticateFinished()
  await ensureCurrentPlayerBuild()
  return authPath === to.fullPath ? undefined : authPath
}

function authenticateFinished() {
  const authenticateFinishedFunctionName = window.testConfig?.authenticateFinishedFunctionName
  if (authenticateFinishedFunctionName) {
    window[authenticateFinishedFunctionName]?.()
  }
}

router.afterEach(() => (isNavigating.value = false))

// TODO: playwright test for this
async function checkForUpdate(to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedGeneric) {
  if (
    localStorage.getItem('swNeedsUpdate') &&
    from.name != null &&
    to.meta?.layout !== 'auth' &&
    !navigator.webdriver
  ) {
    localStorage.removeItem('swNeedsUpdate')
    await updateGlobalServiceWorker()
    location.href = to.fullPath
    return
  }
}
